/**
 * View more / View less (horizontal) – responsive:
 * Desktop: show first N columns (max 4), rest in collapsible.
 * Mobile: show 1 card, rest in collapsible.
 */
const MEDIA_QUERY = globalThis.matchMedia('(min-width: 750px)');

/**
 * @param {HTMLElement} el
 * @returns {number}
 */
function getLimit(el) {
  const desktop = Number.parseInt(el.dataset.desktopLimit ?? '4', 10) || 4;
  const mobile = Number.parseInt(el.dataset.mobileLimit ?? '1', 10) || 1;
  return MEDIA_QUERY.matches ? desktop : mobile;
}

function initViewMoreLessHorizontal() {
  const containers = document.querySelectorAll('.view-more-less-horizontal');

  containers.forEach((container) => {
    const el = /** @type {HTMLElement} */ (container);
    const viewMoreText = el.dataset.viewMore ?? 'View more';
    const viewLessText = el.dataset.viewLess ?? 'View less';
    const itemsEl = el.querySelector('.view-more-less-horizontal__items');
    const cta = el.querySelector('[data-view-more-less-horizontal-trigger]');

    if (!itemsEl || !cta) return;

    const ctaEl = /** @type {HTMLElement} */ (cta);
    const itemsContainer = /** @type {HTMLElement} */ (itemsEl);

    function getItems() {
      return Array.from(itemsContainer.children).filter(
        (child) => !child.classList.contains('view-more-less-horizontal__collapsible')
      );
    }

    function collectAllItemsInOrder() {
      const items = getItems();
      const collapsible = itemsContainer.querySelector('.view-more-less-horizontal__collapsible');
      const inner = collapsible?.querySelector('.view-more-less-horizontal__collapsible-inner');
      const order = [...items];
      if (inner) {
        const inCollapsible = Array.from(inner.children);
        order.push(...inCollapsible);
      }
      return order;
    }

    /**
     * @param {number} limit
     * @returns {HTMLElement | null}
     */
    function rebuild(limit) {
      const allItems = collectAllItemsInOrder();

      const collapsible = itemsContainer.querySelector('.view-more-less-horizontal__collapsible');
      if (collapsible) collapsible.remove();

      allItems.forEach((item) => itemsContainer.appendChild(item));

      const items = getItems();
      if (items.length <= limit) {
        ctaEl.style.display = 'none';
        return null;
      }

      ctaEl.style.display = '';
      const collapsibleNew = document.createElement('div');
      collapsibleNew.className = 'view-more-less-horizontal__collapsible';
      collapsibleNew.setAttribute('aria-hidden', 'true');
      const collapsibleInner = document.createElement('div');
      collapsibleInner.className = 'view-more-less-horizontal__collapsible-inner';
      const extraItems = items.slice(limit);
      extraItems.forEach((item) => collapsibleInner.appendChild(item));
      collapsibleNew.appendChild(collapsibleInner);
      itemsContainer.appendChild(collapsibleNew);
      return collapsibleNew;
    }

    /** @param {boolean} [shouldScroll] */
    function setCollapsed(shouldScroll) {
      const collapsibleEl = /** @type {HTMLElement | null} */ (itemsContainer.querySelector('.view-more-less-horizontal__collapsible'));
      if (!collapsibleEl) return;
      collapsibleEl.style.height = '0';
      collapsibleEl.classList.remove('view-more-less-horizontal__collapsible--open');
      collapsibleEl.setAttribute('aria-hidden', 'true');
      ctaEl.textContent = viewMoreText;
      ctaEl.setAttribute('aria-expanded', 'false');
      el.classList.remove('view-more-less-horizontal--expanded');
      el.classList.add('view-more-less-horizontal--collapsed');
      if (shouldScroll) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    function setExpanded() {
      const collapsibleEl = /** @type {HTMLElement | null} */ (itemsContainer.querySelector('.view-more-less-horizontal__collapsible'));
      if (!collapsibleEl) return;
      const inner = collapsibleEl.querySelector('.view-more-less-horizontal__collapsible-inner');
      if (inner) {
        collapsibleEl.style.overflow = 'visible';
        collapsibleEl.style.height = 'auto';
        const h = inner.scrollHeight;
        collapsibleEl.style.height = '0';
        collapsibleEl.style.overflow = '';
        requestAnimationFrame(() => {
          collapsibleEl.style.height = `${h}px`;
        });
      }
      collapsibleEl.classList.add('view-more-less-horizontal__collapsible--open');
      collapsibleEl.setAttribute('aria-hidden', 'false');
      ctaEl.textContent = viewLessText;
      ctaEl.setAttribute('aria-expanded', 'true');
      el.classList.add('view-more-less-horizontal--expanded');
      el.classList.remove('view-more-less-horizontal--collapsed');
    }

    ctaEl.addEventListener('click', () => {
      const isExpanded = el.classList.contains('view-more-less-horizontal--expanded');
      if (isExpanded) setCollapsed(true);
      else setExpanded();
    });

    let currentLimit = getLimit(el);
    rebuild(currentLimit);
    setCollapsed();

    MEDIA_QUERY.addEventListener('change', () => {
      const newLimit = getLimit(el);
      if (newLimit === currentLimit) return;
      currentLimit = newLimit;
      rebuild(currentLimit);
      setCollapsed();
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initViewMoreLessHorizontal);
} else {
  initViewMoreLessHorizontal();
}

document.addEventListener('shopify:section:load', initViewMoreLessHorizontal);
